import React, { useMemo } from 'react';

export const BANKS = [
  'BCP',
  'BBVA',
  'Interbank',
  'Scotiabank',
  'BanBif',
  'Banco de la Nación',
  'Otro',
];

const ACCOUNT_GROUPS = [3, 10, 1, 2]; // XXX-XXXXXXXXXX-X-XX → 16 digits
const CCI_GROUPS = [4, 4, 4, 4, 4];    // XXXX XXXX XXXX XXXX XXXX → 20 digits
const ACCOUNT_SEP = '-';
const CCI_SEP = ' ';
const BANK_DELIM = ' — ';

function totalLen(groups) {
  return groups.reduce((s, g) => s + g, 0);
}

function formatWith(separator, digits, groups) {
  const parts = [];
  let i = 0;
  for (const g of groups) {
    if (i >= digits.length) break;
    parts.push(digits.slice(i, i + g));
    i += g;
  }
  if (i < digits.length) parts.push(digits.slice(i));
  return parts.join(separator);
}

export function parseStoredBankAccount(stored) {
  if (!stored) return { bank: 'BCP', accountDigits: '' };
  const s = String(stored);
  const idx = s.indexOf(BANK_DELIM);
  if (idx >= 0) {
    const bank = s.slice(0, idx).trim();
    const rest = s.slice(idx + BANK_DELIM.length).trim();
    const digits = rest.replace(/\D/g, '').slice(0, totalLen(ACCOUNT_GROUPS));
    return { bank: BANKS.includes(bank) ? bank : 'Otro', accountDigits: digits };
  }
  return { bank: 'Otro', accountDigits: s.replace(/\D/g, '').slice(0, totalLen(ACCOUNT_GROUPS)) };
}

export function composeStoredBankAccount(bank, digits) {
  // Preserve the bank selection even when the user hasn't entered any digits
  // yet, otherwise switching the bank in the dropdown silently wipes the
  // value back to '' and the next read falls back to BCP (the parser default).
  if (!bank && !digits) return '';
  const formatted = digits ? formatWith(ACCOUNT_SEP, digits, ACCOUNT_GROUPS) : '';
  if (!bank || bank === 'Otro') return formatted;
  // The delimiter is required for round-tripping — `parseStoredBankAccount`
  // looks for it to recognize the bank name. Keep it even when digits === ''.
  return `${bank}${BANK_DELIM}${formatted}`;
}

export function formatCciDisplay(stored) {
  if (!stored) return '';
  const digits = String(stored).replace(/\D/g, '').slice(0, totalLen(CCI_GROUPS));
  return formatWith(CCI_SEP, digits, CCI_GROUPS);
}

export function formatBankAccountDisplay(stored) {
  if (!stored) return '';
  const { bank, accountDigits } = parseStoredBankAccount(stored);
  if (!accountDigits) return stored;
  const formatted = formatWith(ACCOUNT_SEP, accountDigits, ACCOUNT_GROUPS);
  return bank === 'Otro' ? formatted : `${bank}${BANK_DELIM}${formatted}`;
}

export function BankAccountField({ account, cci, onAccountChange, onCciChange, labelStyle, inputStyle }) {
  const { bank, accountDigits } = useMemo(() => parseStoredBankAccount(account), [account]);
  const cciDigits = useMemo(
    () => (cci || '').replace(/\D/g, '').slice(0, totalLen(CCI_GROUPS)),
    [cci]
  );

  function setBank(newBank) {
    onAccountChange(composeStoredBankAccount(newBank, accountDigits));
  }

  function onTypeAccount(e) {
    const raw = e.target.value.replace(/\D/g, '').slice(0, totalLen(ACCOUNT_GROUPS));
    onAccountChange(composeStoredBankAccount(bank, raw));
  }

  function onTypeCci(e) {
    const raw = e.target.value.replace(/\D/g, '').slice(0, totalLen(CCI_GROUPS));
    onCciChange(formatWith(CCI_SEP, raw, CCI_GROUPS));
  }

  const accountDisplay = formatWith(ACCOUNT_SEP, accountDigits, ACCOUNT_GROUPS);
  const cciDisplay = formatWith(CCI_SEP, cciDigits, CCI_GROUPS);
  const mergedInput = { ...styles.input, ...(inputStyle || {}) };
  const mergedLabel = { ...styles.label, ...(labelStyle || {}) };

  return (
    <div style={styles.wrap}>
      <div style={styles.accountRow}>
        <div style={styles.subGroup}>
          <span style={mergedLabel}>Banco</span>
          <select
            style={{ ...mergedInput, ...styles.bankSelect }}
            value={bank}
            onChange={(e) => setBank(e.target.value)}
          >
            {BANKS.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div style={styles.subGroup}>
          <span style={mergedLabel}>Número de cuenta</span>
          <input
            type="text"
            inputMode="numeric"
            value={accountDisplay}
            onChange={onTypeAccount}
            placeholder="999-9999999999-9-99"
            style={{ ...mergedInput, ...styles.accountInput }}
          />
        </div>
      </div>
      <div style={styles.subGroup}>
        <span style={mergedLabel}>CCI (opcional)</span>
        <input
          type="text"
          inputMode="numeric"
          value={cciDisplay}
          onChange={onTypeCci}
          placeholder="9999 9999 9999 9999 9999"
          style={{ ...mergedInput, ...styles.cciInput }}
        />
      </div>
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 10 },
  accountRow: { display: 'grid', gridTemplateColumns: 'minmax(140px, 180px) 1fr', gap: 10 },
  subGroup: { display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 },
  label: { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.02em' },
  input: {
    padding: '8px 11px',
    borderRadius: 7,
    border: '1px solid var(--border)',
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    color: 'var(--text-primary)',
    background: '#FDFBF8',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  bankSelect: {},
  accountInput: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    letterSpacing: '0.03em',
  },
  cciInput: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    letterSpacing: '0.03em',
  },
};
